'use client'

import { useCallback, useEffect, useState } from 'react'
import { AlertCircle, Loader2 } from 'lucide-react'
import { DomainIntelligencePanel } from '../ProcessedEntry/DomainEntry/DomainIntelligencePanel'
import { BrowserDataPanel } from '../ProcessedEntry/DomainEntry/BrowserDataPanel'
import { ProbeRunsPanel } from '../ProcessedEntry/DomainEntry/ProbeRunsPanel'
import { ProcessedEntryTimeline } from '../ProcessedEntry/DomainEntry/ProcessedEntryTimeline'
import { LatestObtainedImagesPanel } from '../ProcessedEntry/DomainEntry/LatestObtainedImagesPanel'
import { watchClasses } from '../ProcessedEntry/DomainEntry/processedTypes'
import type { BrowserRow, EntryDetailData, ProcessedEntry, ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import type { EntryRow } from './entryTypes'

// Domain evidence for ONE report of a campaign, scoped per domain link.
//
// A campaign's members are separate reports, and each one carries its own
// user_entry_domain row — its own reported path, its own browser captures,
// its own probe runs. Screenshots hang off entry_browser_data keyed by that
// link id, so two reports of the same host can hold completely different
// images. Rendering a single campaign-wide evidence block would therefore
// have to pick one report's link and silently drop the rest, which is why
// this is per report (and, within a report, per domain link).
//
// Fetched here rather than by the campaign page because CollapsibleSection
// only mounts its children while open — so a campaign with 20 reports issues
// no evidence requests at all until an operator actually expands one.

export function CampaignReportEvidence({
  entry,
  theme,
  getScreenshotToken,
}: {
  entry: EntryRow
  theme: ProcessedTheme
  getScreenshotToken: () => Promise<string>
}) {
  const c = watchClasses(theme)
  const [data, setData] = useState<EntryDetailData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(`/api/campaigns/ctt/entry-domains?userEntryId=${encodeURIComponent(entry.id)}`)
      const result = await res.json()
      if (result.success) setData(result.data)
      else setError(result.reason ?? 'Failed to load domain evidence')
    } catch {
      setError('Network error loading domain evidence')
    } finally {
      setLoading(false)
    }
  }, [entry.id])

  useEffect(() => { load() }, [load])

  // Minimal ProcessedEntry — the two panels that take one only read
  // createdAt/headline/title/originalUrl off it, same as the Workbench's own
  // synthetic adapter does.
  const flat = (entry.processed_entries_flat ?? {}) as Record<string, any>
  const processedEntry: ProcessedEntry = {
    id: entry.id,
    createdAt: (flat.created_at ?? entry.created_at ?? new Date().toISOString()) as string,
    headline: flat.headline ?? null,
    title: flat.title ?? null,
    messageContent: flat.message_content ?? null,
    sender: flat.sender ?? null,
    processedInputDescription: null,
    postType: flat.post_type ?? null,
    platformNameCode: flat.platform_name_code ?? null,
    platformName: flat.platform_name ?? null,
    isScam: flat.is_scam === true,
    userEntryId: entry.id,
  }

  if (loading) {
    return (
      <div className={`flex items-center gap-2 px-1 py-3 font-mono text-[11px] uppercase tracking-widest ${c.faint}`}>
        <Loader2 className="h-3 w-3 animate-spin" /> Loading domain evidence…
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-danger-surface px-3 py-2 text-[12px] text-destructive">
        <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>{error}</span>
      </div>
    )
  }

  const domains = data?.domains ?? []
  if (domains.length === 0) {
    return <p className={`px-1 py-2 text-[12px] ${c.faint}`}>No domain registered on this report.</p>
  }

  return (
    <div className="space-y-4">
      {domains.map((domain) => {
        // Scoped to THIS link, unlike the entry detail page which merges every
        // domain's rows together: here the whole point is telling the reports'
        // (and paths') evidence apart.
        const browserRows = (data?.browserData ?? []).filter((r) => r.entryDomainId === domain.id)
        const latestBrowser = browserRows.reduce<BrowserRow | null>((latest, row) =>
          !latest || new Date(row.createdAt).getTime() > new Date(latest.createdAt).getTime() ? row : latest, null)
        const probeRuns = (data?.probeRuns ?? [])
          .filter((r) => r.entryDomainId === domain.id)
          .sort((a, b) => {
            const nodeCompare = (a.probeNode ?? '').localeCompare(b.probeNode ?? '')
            if (nodeCompare !== 0) return nodeCompare
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          })

        return (
          <div key={domain.id}>
            {domains.length > 1 && (
              <div className={`mb-2 font-mono text-[10px] break-all ${c.muted}`}>{domain.originalUrl ?? domain.domainId}</div>
            )}
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
              <ProcessedEntryTimeline entry={processedEntry} domain={domain} browserData={browserRows} theme={theme} />
              <LatestObtainedImagesPanel
                entryId={entry.id}
                userEntryDomainId={domain.id}
                latestBrowser={latestBrowser}
                theme={theme}
                getToken={getScreenshotToken}
              />
              <DomainIntelligencePanel entry={processedEntry} domain={domain} theme={theme} />
              <BrowserDataPanel curBrowserData={browserRows} latestBrowser={latestBrowser} theme={theme} />
              <ProbeRunsPanel curProbeRuns={probeRuns} theme={theme} />
            </div>
          </div>
        )
      })}
    </div>
  )
}
